/**
 * Support — the inbound support-email queue. Lists emails sent to the support
 * alias (recorded on event.classification.support by the orchestrator), with a
 * severity-triaged view and a "mark handled" toggle. Read + handled-state only;
 * replies happen in the support@ Gmail inbox where the thread lives.
 */
import { useState } from "react";
import { Chip, Empty, ErrorNote, Panel, useApi } from "../components/ui.js";
import { api, ApiError } from "../lib/api.js";
import { fmtDateTime } from "../lib/format.js";
import type { SupportEmailRow } from "../lib/types.js";

const TABS = ["open", "all"] as const;

function sevTone(s: string): "red" | "amber" {
  return s === "critical" ? "red" : "amber";
}

export function Support() {
  const [tab, setTab] = useState<(typeof TABS)[number]>("open");
  const list = useApi<{ support: SupportEmailRow[] }>(`/api/support?status=${tab}&limit=50`);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const items = list.data?.support ?? [];
  const open = items.find((r) => r.id === openId) ?? null;

  async function setHandled(r: SupportEmailRow, handled: boolean) {
    setBusy(true);
    setNotice(null);
    try {
      await api.post(`/api/support/${r.id}/handle`, { occurredAt: r.occurredAt, handled });
      setNotice(`#${r.id} ${handled ? "marked handled" : "reopened"}`);
      if (handled && tab === "open") setOpenId(null);
      list.reload();
    } catch (e) {
      setNotice(e instanceof ApiError && e.status === 404
        ? `#${r.id} not found — refreshed`
        : `failed: ${e instanceof Error ? e.message : String(e)}`);
      list.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Support</h1>
        <span className="stamp">inbound support queue · reply from the support@ inbox in Gmail</span>
      </div>

      <div className="row" style={{ marginBottom: 14 }}>
        {TABS.map((t) => (
          <button key={t} className={`btn sm ${tab === t ? "primary" : ""}`} onClick={() => { setTab(t); setOpenId(null); }}>{t}</button>
        ))}
        <button className="btn sm" onClick={() => list.reload()} disabled={list.loading}>refresh</button>
        {notice ? <span className="tag" style={{ color: "var(--green)" }}>{notice}</span> : null}
      </div>

      {list.error ? <ErrorNote error={list.error} /> : null}

      <div className="grid cols-2">
        <Panel title={`${tab} support`}>
          {items.length === 0 ? (
            <Empty>{list.loading ? "loading…" : tab === "open" ? "no open support requests" : "no support emails yet"}</Empty>
          ) : (
            <table className="data">
              <thead><tr><th>received</th><th>sev</th><th>category</th><th>from</th><th>subject</th><th>ack</th><th>status</th></tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => setOpenId(r.id)}
                      style={openId === r.id ? { outline: "1px solid var(--red-dim)" } : undefined}>
                    <td className="num dim">{fmtDateTime(r.occurredAt)}</td>
                    <td><Chip tone={sevTone(r.severity)}>{r.severity}</Chip></td>
                    <td className="dim">{r.category}</td>
                    <td>{r.fromName || r.fromEmail || "—"}</td>
                    <td>{r.subject || "(no subject)"}</td>
                    <td className="dim">{r.ackSent ? "✓" : "—"}</td>
                    <td>{r.handled ? <Chip tone="green">handled</Chip> : <Chip tone="amber">open</Chip>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title={open ? (open.subject || "(no subject)") : "detail"}>
          {!open ? (
            <Empty>select a support email</Empty>
          ) : (
            <>
              <div className="row" style={{ gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                <Chip tone={sevTone(open.severity)}>{open.severity}</Chip>
                <Chip tone="blue">{open.category}</Chip>
                <Chip tone={open.ackSent ? "green" : "amber"}>{open.ackSent ? "acked" : "no ack"}</Chip>
                <Chip tone={open.handled ? "green" : "amber"}>{open.handled ? "handled" : "open"}</Chip>
              </div>
              <div className="mono" style={{ fontSize: 12.5 }}>{open.fromName || "—"} · {open.fromEmail || "—"}</div>
              <div className="dim" style={{ fontSize: 12, marginBottom: 10 }}>
                received {fmtDateTime(open.occurredAt)}
                {open.handled && open.handledAt ? ` · handled ${fmtDateTime(open.handledAt)}` : ""}
              </div>
              <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{open.preview || "(no message body)"}</p>
              <div className="row" style={{ marginTop: 12, alignItems: "center", gap: 10 }}>
                {open.handled ? (
                  <button className="btn sm" disabled={busy} onClick={() => setHandled(open, false)}>reopen</button>
                ) : (
                  <button className="btn sm primary" disabled={busy} onClick={() => setHandled(open, true)}>mark handled</button>
                )}
                <span className="dim" style={{ fontSize: 12 }}>Reply from the support@ inbox in Gmail — this just tracks status.</span>
              </div>
            </>
          )}
        </Panel>
      </div>
    </>
  );
}
